import { useEffect, useState, useCallback, useMemo, type ReactNode, type MouseEvent } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Menu,
  MenuItem,
  Stack,
  Chip,
} from '@mui/material';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../../../utils/axiosClient';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import DateRangeSelector from '../../../components/DateRangeSelector';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import { PointhomeFunnelParticipationData } from '../../../types/pointhomeFunnel.types';
import PointhomeParticipationDashboard from '../../../components/pointhome/contents/participation/PointhomeParticipationDashboard';

export default function PointhomeParticipation() {
  const [participationData, setParticipationData] = useState<PointhomeFunnelParticipationData>(
    [] as PointhomeFunnelParticipationData,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [osAnchorEl, setOsAnchorEl] = useState<null | HTMLElement>(null);
  const [serviceAnchorEl, setServiceAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [contentAnchorEl, setContentAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState<number>(0);
  const [selectedContentFilter, setSelectedContentFilter] = useState<string>('황금티켓');

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...response.data]);
      })
      .catch(() => {
        // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
      });
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const OS_OPTIONS = useMemo(
    () => [
      { label: 'AOS + IOS', value: 'all' },
      { label: 'AOS', value: 'aos' },
      { label: 'IOS', value: 'ios' },
    ],
    [],
  );

  const SERVICE_FILTER_OPTIONS = useMemo(
    () => [
      { label: '포인트홈 + 룰렛', value: 0 },
      { label: '포인트홈', value: 1 },
      { label: '룰렛', value: 2 },
    ],
    [],
  );

  const CONTENT_FILTER_OPTIONS = useMemo(
    () => [
      { label: '황금티켓', value: '황금티켓' },
      { label: '티켓', value: '티켓' },
      { label: '룰렛', value: '룰렛' },
      { label: 'AI 주식 종목 추천', value: 'AI 주식 종목 추천' },
      { label: '오늘의 상식', value: '오늘의 상식' },
      { label: '행운의 포춘쿠키', value: '행운의 포춘쿠키' },
      { label: '달고나 게임', value: '달고나 게임' },
      { label: '오늘의 총운', value: '오늘의 총운' },
      { label: '리워드 뉴스', value: '리워드 뉴스' },
      { label: '오늘의 띠별 운세', value: '오늘의 띠별 운세' },
      { label: '버즈빌 네이버 플레이스', value: '버즈빌 네이버 플레이스' },
      { label: '주간미션', value: '주간미션' },
      { label: '버즈빌 미션', value: '버즈빌 미션' },
      { label: '출석체크', value: '출석체크' },
      { label: '오퍼월', value: '오퍼월' },
      { label: '뉴스', value: '뉴스' },
    ],
    [],
  );

  const fetchParticipationData = useCallback(() => {
    if (!startDate || !endDate) return;
    if (selectedApp === null) return;

    setLoading(true);

    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const params: Record<string, string> = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      ...(selectedApp ? { app: selectedApp === '앱 전체' ? 'all' : selectedApp } : {}),
      content: selectedContentFilter,
      os_type: selectedOs,
      service_filter: String(selectedServiceFilter),
    };

    apiClient
      .get(`/api/v1/analytics/pointhome/participation`, { params })
      .then((response) => {
        setParticipationData(response.data);
        setErrorMessage('');
      })
      .catch(() => {
        setParticipationData([] as PointhomeFunnelParticipationData);
        setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs, selectedServiceFilter, selectedContentFilter]);

  useEffect(() => {
    if (startDate && endDate && selectedApp != null) {
      fetchParticipationData();
    }
  }, [
    startDate,
    endDate,
    selectedApp,
    selectedOs,
    selectedServiceFilter,
    selectedContentFilter,
    fetchParticipationData,
  ]);

  const selectedOsLabel =
    OS_OPTIONS.find((option) => option.value === selectedOs)?.label ?? 'OS 전체';
  const selectedServiceLabel =
    SERVICE_FILTER_OPTIONS.find((option) => option.value === selectedServiceFilter)?.label ??
    '포인트홈 + 룰렛';
  const selectedContentLabel =
    CONTENT_FILTER_OPTIONS.find((option) => option.value === selectedContentFilter)?.label ??
    '황금티켓';

  const renderFilterButton = ({
    icon,
    label,
    value,
    onClick,
  }: {
    icon: ReactNode;
    label: string;
    value: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  }) => (
    <Button
      variant="outlined"
      onClick={onClick}
      endIcon={<KeyboardArrowDownIcon />}
      sx={{
        borderColor: '#d0d7e2',
        borderRadius: 2,
        textTransform: 'none',
        color: '#1f2a37',
        minWidth: 220,
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon}
        <Stack direction="column" alignItems="flex-start">
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 12 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );

  return (
    <Box
      sx={{
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.3s ease',
        minHeight: '100vh',
      }}
    >
      <Box alignItems={'center'} display={'flex'}>
        <Box>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={() => {
              if (startDate && endDate) {
                fetchParticipationData();
              }
            }}
          />
        </Box>
        <Box
          sx={{
            ml: 3,
          }}
        >
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(setApp) => {
              setSelectedApp(setApp);
            }}
            onSearch={fetchParticipationData}
          />
        </Box>
      </Box>

      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 앱 선택 (Chip) */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0', mb: 2 }} />

        <Box mt={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {renderFilterButton({
              icon: <LaptopMacOutlinedIcon fontSize="small" />,
              label: 'OS',
              value: selectedOsLabel,
              onClick: (event) => setOsAnchorEl(event.currentTarget),
            })}
            {renderFilterButton({
              icon: <EmojiObjectsOutlinedIcon fontSize="small" />,
              label: '서비스',
              value: selectedServiceLabel,
              onClick: (event) => setServiceAnchorEl(event.currentTarget),
            })}
            {renderFilterButton({
              icon: <EmojiObjectsOutlinedIcon fontSize="small" />,
              label: '콘텐츠',
              value: selectedContentLabel,
              onClick: (event) => setContentAnchorEl(event.currentTarget),
            })}
          </Stack>

          <Menu
            anchorEl={osAnchorEl}
            open={Boolean(osAnchorEl)}
            onClose={() => setOsAnchorEl(null)}
          >
            {OS_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedOs}
                onClick={() => {
                  setOsAnchorEl(null);
                  setSelectedOs(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={serviceAnchorEl}
            open={Boolean(serviceAnchorEl)}
            onClose={() => setServiceAnchorEl(null)}
          >
            {SERVICE_FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedServiceFilter}
                onClick={() => {
                  setServiceAnchorEl(null);
                  setSelectedServiceFilter(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>

          <Menu
            anchorEl={contentAnchorEl}
            open={Boolean(contentAnchorEl)}
            onClose={() => setContentAnchorEl(null)}
          >
            {CONTENT_FILTER_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === selectedContentFilter}
                onClick={() => {
                  setContentAnchorEl(null);
                  setSelectedContentFilter(option.value);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : startDate && endDate ? (
          <PointhomeParticipationDashboard
            data={participationData}
            selectedApp={selectedApp}
            startDate={startDate}
            endDate={endDate}
            selectedContentFilter={selectedContentFilter}
          />
        ) : null}
      </Box>
    </Box>
  );
}
